import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const SGT_TIMEZONE = "Asia/Singapore";

/**
 * Converts a UTC ISO-8601 string (e.g. 2026-08-15T00:00:00Z) to a Dayjs object in Singapore Time (SGT / UTC+8).
 */
export function utcIsoToSgtDayjs(isoString?: string | null): Dayjs | null {
  if (!isoString) return null;
  const parsed = dayjs.utc(isoString);
  if (!parsed.isValid()) return null;
  return parsed.tz(SGT_TIMEZONE);
}

/**
 * Converts a Dayjs object (representing date & time chosen in SGT) to a UTC ISO-8601 string (ending in Z).
 */
export function sgtDayjsToUtcIso(date?: Dayjs | null): string | null {
  if (!date || !dayjs(date).isValid()) return null;
  // Interpret the date string formatted as YYYY-MM-DD HH:mm:ss within SGT timezone and convert to UTC ISO string
  const formattedString = date.format("YYYY-MM-DD HH:mm:ss");
  const inSgt = dayjs.tz(formattedString, SGT_TIMEZONE);
  return inSgt.utc().toISOString();
}

/**
 * Formats a UTC ISO-8601 string for display in SGT with time.
 * E.g., "15-Aug-2026 14:30 SGT"
 */
export function formatSgtDateTime(isoString?: string | null): string {
  if (!isoString) return "-";
  const parsed = dayjs.utc(isoString);
  if (!parsed.isValid()) return "-";
  return parsed.tz(SGT_TIMEZONE).format("DD-MMM-YYYY HH:mm [SGT]");
}

/**
 * Formats a UTC ISO-8601 string for display in SGT with date only.
 * E.g., "15-Aug-2026"
 */
export function formatSgtDate(isoString?: string | null): string {
  if (!isoString) return "-";
  const parsed = dayjs.utc(isoString);
  if (!parsed.isValid()) return "-";
  return parsed.tz(SGT_TIMEZONE).format("DD-MMM-YYYY");
}

/**
 * Checks if a UTC ISO date string is already in the past.
 */
export function isUtcDateInPast(isoString?: string | null): boolean {
  if (!isoString) return false;
  const parsed = dayjs.utc(isoString);
  if (!parsed.isValid()) return false;
  return parsed.isBefore(dayjs.utc());
}
